import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { calculerURSSAF, calculerBaseImposableMicroBNC, calculerIRProgressif } from "@/lib/fiscalCalculations";
import { calculURSSAF_bnc } from "@/utils/fiscal";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", blue: "#2E6DA4",
  muted: "#6B85A3", border: "#D6E4F0", bg: "#F4F8FC", white: "#FFFFFF",
  lightTeal: "#EAF7F5", altRow: "#F9FBFC",
};

const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n) + "\u00a0€";


type YearData = {
  ca: number;
  regime: string;
  charges: number;
  revenuNet: number;
  evolution: number | null;
};

export default function ProgressionAnnuelle() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState<Record<number, YearData>>({});
  const [loaded, setLoaded] = useState(false);
  const currentYear = new Date().getFullYear();
  const { plan } = useSubscription();
  const isPro = plan === "pro";
  const isEssentiel = !isPro;
  const navigate = useNavigate();
  // Essentiel: only current year; Pro: multi-year comparison
  const years = isEssentiel ? [currentYear] : (isMobile ? [currentYear - 1, currentYear] : [currentYear - 2, currentYear - 1, currentYear]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      // Fetch all recettes
      const { data: recettes } = await supabase
        .from("recettes")
        .select("date, montant")
        .eq("user_id", user.id)
        .order("date", { ascending: true })
        .limit(500);

      // Group by year
      const parAnnee: Record<number, number> = {};
      (recettes || []).forEach((r: any) => {
        const annee = new Date(r.date).getFullYear();
        parAnnee[annee] = (parAnnee[annee] || 0) + Number(r.montant);
      });

      // Fetch charges for all years
      const { data: charges } = await supabase
        .from("depenses_2035")
        .select("annee, montant")
        .eq("user_id", user.id)
        .limit(500);

      const chargesParAnnee: Record<number, number> = {};
      (charges || []).forEach((c: any) => {
        chargesParAnnee[c.annee] = (chargesParAnnee[c.annee] || 0) + Number(c.montant);
      });

      // Profile (current regime - we use it for all years as approximation)
      const { data: profil } = await supabase
        .from("profils")
        .select("statut_fiscal")
        .eq("user_id", user.id)
        .maybeSingle();

      const regime = profil?.statut_fiscal || "micro-bnc";
      const isMicro = regime !== "bnc-classique";

      if (cancelled) return;

      const result: Record<number, YearData> = {};
      const allYears = [currentYear - 2, currentYear - 1, currentYear];

      allYears.forEach((y, idx) => {
        const ca = parAnnee[y] || 0;
        const ch = chargesParAnnee[y] || 0;

        // Revenu net: CA - URSSAF - IR
        const beneficeNet = Math.max(0, ca - ch);
        const urssaf = isMicro ? calculerURSSAF(ca) : calculURSSAF_bnc(beneficeNet);
        const beneficeImposable = isMicro ? calculerBaseImposableMicroBNC(ca) : Math.max(0, beneficeNet - urssaf);
        const ir = calculerIRProgressif(beneficeImposable);
        const revenuNet = ca - urssaf - ch - ir;

        const prevCa = idx > 0 ? (parAnnee[allYears[idx - 1]] || 0) : 0;
        const evolution = idx === 0 || prevCa === 0 ? null : ((ca - prevCa) / prevCa) * 100;

        result[y] = { ca, regime: isMicro ? "Micro-BNC" : "BNC Classique", charges: ch, revenuNet: Math.max(0, revenuNet), evolution };
      });

      setData(result);
      setLoaded(true);
    };

    load();
    return () => { cancelled = true; };
  }, [user]);

  if (!loaded) return null;

  const ca2025 = data[currentYear - 1]?.ca || 0;
  const ca2026 = data[currentYear]?.ca || 0;
  const moisRestants = 12 - (new Date().getMonth() + 1);

  const lines = [
    { label: "CA annuel", icon: "💰", key: "ca" as const },
    { label: "Revenu net estimé", icon: "✨", key: "revenuNet" as const },
    { label: "Évolution CA", icon: "📊", key: "evolution" as const },
    { label: "Régime fiscal", icon: "📋", key: "regime" as const },
  ];

  return (
    <div className="mt-8">
      <div className="mb-4">
        <div className="text-lg font-bold" style={{ color: C.navy }}>📈 Ma progression annuelle</div>
        <div className="text-sm" style={{ color: C.muted }}>Comparez votre activité d'une année à l'autre</div>
      </div>

      <div className="rounded-xl bg-white shadow-sm border overflow-hidden" style={{ borderColor: C.border, borderRadius: 12 }}>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-xs" style={{ color: C.muted }}></th>
              {years.map(y => (
                <th
                  key={y}
                  className="text-right px-4 py-3 font-bold text-sm"
                  style={{
                    color: y === currentYear ? C.teal : C.navy,
                    backgroundColor: y === currentYear ? C.lightTeal : "transparent",
                  }}
                >
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => (
              <tr key={line.key} style={{ backgroundColor: idx % 2 === 1 ? C.altRow : C.white }}>
                <td className="px-4 py-3 text-left font-medium text-xs whitespace-nowrap" style={{ color: C.navy }}>
                  <span className="mr-1.5">{line.icon}</span>{line.label}
                </td>
                {years.map(y => {
                  const d = data[y];
                  const hasCa = d && d.ca > 0;

                  let content: React.ReactNode;
                  if (line.key === "ca") {
                    content = hasCa ? fmt(d.ca) : <span className="italic" style={{ color: C.muted }}>—</span>;
                  } else if (line.key === "revenuNet") {
                    content = hasCa ? fmt(d.revenuNet) : <span className="italic" style={{ color: C.muted }}>—</span>;
                  } else if (line.key === "evolution") {
                    if (d?.evolution === null || d?.evolution === undefined) {
                      content = <span className="italic" style={{ color: C.muted }}>—</span>;
                    } else {
                      const pct = Math.round(d.evolution);
                      content = (
                        <span style={{ color: pct >= 0 ? C.teal : "#E63946", fontWeight: 600 }}>
                          {pct >= 0 ? "+" : ""}{pct}%
                        </span>
                      );
                    }
                  } else if (line.key === "regime") {
                    content = hasCa ? d.regime : <span className="italic" style={{ color: C.muted }}>—</span>;
                  }

                  return (
                    <td
                      key={y}
                      className="px-4 py-3 text-right text-sm font-semibold"
                      style={{
                        color: C.navy,
                        backgroundColor: y === currentYear ? C.lightTeal : "transparent",
                      }}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Message motivant */}
      <div className="mt-3 text-sm italic" style={{ color: C.teal }}>
        {ca2025 > 0 && ca2026 > ca2025
          ? "🎉 Votre activité progresse — continuez comme ça !"
          : ca2025 > 0 && ca2026 <= ca2025
            ? `💪 L'année n'est pas terminée — vous avez encore ${moisRestants} mois pour progresser.`
            : "📊 Revenez l'année prochaine pour voir votre progression !"}
      </div>

      {/* Banner multi-années pour essentiel */}
      {isEssentiel && (
        <div className="mt-3 rounded-xl px-4 py-3 flex items-center justify-between" style={{ backgroundColor: "#FFF8F0", border: "1px solid #F4A261" }}>
          <span className="text-sm" style={{ color: "#C46200" }}>
            📈 Comparez vos années avec le plan Pro
          </span>
          <button
            onClick={() => navigate("/pricing")}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-80"
            style={{ color: "#2A9D8F" }}
          >
            Débloquer →
          </button>
        </div>
      )}
    </div>
  );
}
